// session-reset.js -- the in-app browser's sportsbook session (#1415 spec §3; plan D6).
// Single job: clear the sportsbook cookies and cache through the InAppBrowser plugin, and make sure the session
// in the browser belongs to the SlipTrack user about to sync. With no browser open the plugin clears
// WKWebsiteDataStore.default() (InAppBrowserPlugin.swift:318-329): cookies, then the disk and memory cache. Site
// storage (localStorage, IndexedDB) is NOT cleared, which is why SlipTrack's own session survives; a book that kept
// its login there would survive too (the PR 3 device check covers DK, MGM and FanDuel). Never throws: failures,
// including a plugin call that does not settle within the deadline, are warnings plus { ok: false }.
import { Capacitor } from '@capacitor/core';
import { InAppBrowser } from '@capgo/capacitor-inappbrowser';
import * as Sentry from '../errorReporting';
import { storageGet, storageSet } from '../platform';

const OWNER_KEY = 'slipsync_session_owner';
const UNKNOWN_OWNER = ''; // never a user id: the next sync by anyone clears first
export const SIGN_OUT_DEADLINE_MS = 10000;

export const canSignOutOfSportsbooks = () => Capacitor.getPlatform() === 'ios';

export async function signOutOfSportsbooks({ timeoutMs = SIGN_OUT_DEADLINE_MS } = {}) {
  // Codex P1 (#1476): no one owns the session from here on, whatever the clear does. A failed or stalled clear
  // then still forces the next sync (by anyone, the same user included) to clear first. Best effort: an unwritable
  // owner key is caught by ensureSportsbookSessionOwner, which checks its own writes.
  storageSet(OWNER_KEY, UNKNOWN_OWNER);
  let timer;
  const deadline = new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('sportsbook sign-out timed out')), timeoutMs); });
  try {
    await Promise.race([deadline, (async () => { await InAppBrowser.clearAllCookies(); await InAppBrowser.clearCache(); })()]);
    return { ok: true };
  } catch {
    Sentry.captureMessage('slipsync: sportsbook sign-out failed', { level: 'warning', tags: { function: 'signOutOfSportsbooks' } });
    return { ok: false };
  } finally {
    clearTimeout(timer);
  }
}

// One SlipTrack user per sportsbook session on a phone: a different (or unknown) owner is signed out first, so
// user B can never sync user A's sportsbook history. The owner is marked unknown BEFORE the clear and moves to the
// new user only after a successful clear and a successful write (pre-Codex Sec F1): a failed clear or a failed
// write can never leave a user recorded as the owner of a session that is not theirs. Storage that cannot be
// written at all means ownership cannot be tracked, so nothing opens (fail closed).
export async function ensureSportsbookSessionOwner(userId) {
  if (storageGet(OWNER_KEY) === userId) return { ok: true, cleared: false };
  if (!storageSet(OWNER_KEY, UNKNOWN_OWNER)) return { ok: false, cleared: false };
  const { ok } = await signOutOfSportsbooks();
  if (!ok) return { ok: false, cleared: false };
  if (!storageSet(OWNER_KEY, userId)) return { ok: false, cleared: true };
  return { ok: true, cleared: true };
}
